import { z } from 'zod';
import { LABEL_PURGING_TENANT_IDS } from './labelPurge';
import { TEST_MODE_BILLING_TENANT_IDS } from './testModeTenants';
import { TenantDisplayStatus } from './tenantDisplayStatus';

/**
 * Regras puras do monitor de padrão de uso por tenant: decide, a partir das
 * impressões diárias de um cliente, se ele saiu da própria rotina.
 *
 * Origem: #886 — 2026-10-08 11:41 +0200 (alertas por padrão de uso para
 * admin + vendedor + gerente).
 *
 * Por quê: o único alerta de uso anterior era o `dailyInactiveTenantsReport`
 * (#275), com limiar absoluto (3 dias civis zerados). Ele não vê um cliente
 * que caiu de ~480 para ~50 etiquetas/semana, nem um cliente que sempre
 * imprime na quarta e nesta quarta não imprimiu — o sinal típico de impressora
 * parada, que o owner quer que o vendedor confirme por telefone. Aqui cada
 * cliente é comparado com o PRÓPRIO histórico.
 *
 * Contrato: funções puras, sem I/O e sem relógio. Quem chama entrega o dia
 * avaliado (`yesterdayYmd`, já no fuso do tenant) e uma função
 * `printedOn(ymd)` que devolve as impressões do dia (doc ausente = 0). Saída:
 * lista de {@link UsageFinding} (vazia = cliente dentro do padrão).
 *
 * Regras (limiares em {@link UsageAlertConfig}, defaults em
 * {@link DEFAULT_USAGE_ALERT_CONFIG}):
 *  - `WEEKLY_DROP` / `WEEKLY_SURGE` — avaliadas sobre a semana FECHADA seg–dom
 *    (decisão do owner, 2026-10-08: semana fechada, não 7 dias móveis — no
 *    máximo um alerta por semana). Em {@link evaluateUsagePattern} só quando
 *    `yesterdayYmd` é domingo (avaliação pontual, a original); em
 *    {@link evaluateRecentUsagePattern} (a usada pelo scheduler desde a #795)
 *    avalia-se SEMPRE o último domingo fechado, todo dia, até a semana seguinte
 *    fechar — o id determinístico do alerta torna a reavaliação inofensiva. Base = MEDIANA das `baselineWeeks` semanas
 *    anteriores: o padrão real deste produto é de picos (um cliente teve 216
 *    num dia e 4 noutro na mesma semana), e a média puxaria a base para cima a
 *    cada pico. Dispara se a semana fica abaixo de `(1 − dropRatio)·base` ou
 *    acima de `(1 + surgeRatio)·base`.
 *  - `HABITUAL_DAY_MISSED` — diário (e, em {@link evaluateRecentUsagePattern},
 *    reavaliado para os últimos {@link USAGE_RECHECK_DAYS} dias): ontem zerado, e aquele dia da semana teve
 *    impressão em ≥ `habitualMinHits` das últimas `habitualLookbackWeeks`
 *    ocorrências. Não dispara quando o cliente já está há
 *    `inactiveStreakDays` dias zerado: repetir todo dia viraria o ruído da
 *    #620. Atenção (auditoria de 2026-10-08, #886, A4): o relatório de
 *    inatividade (#275) só cobre ACTIVE/PAYMENT_PENDING; para cortesia e
 *    parceiro, a parada longa chega ao vendedor pelos dias 1–2 (se forem
 *    habituais) e pela queda semanal da segunda seguinte — não há aviso
 *    diário depois disso, e isso é decisão, não esquecimento.
 *
 * Usado por: scheduler `detectTenantUsageAnomalies` (Functions — grava
 * `usage_alerts` e o resumo ao admin), trigger `crmOnUsageAlertCreated` (CRM
 * Functions — lê `UsageAlertSchema`), `scripts/diag-usage-pattern-backtest.ts`
 * (calibração read-only contra produção).
 *
 * Arquitetura: a fonte é `tenants/{tid}/daily_ops/{yyyy-MM-dd}.totals.printed`
 * — etiquetas (docs), não quantidade nem reimpressão, bucketizadas no fuso do
 * tenant pelo `printedAtMillis`. Por isso os `ymd` aqui são datas civis LOCAIS
 * do tenant, e o dia da semana sai da própria data, sem fuso.
 *
 * Dicas: os pisos (`minActiveBaselineWeeks`, `minWeeklyBaseline`,
 * `habitualMinHits`) são o que impede cliente novo ou de volume ínfimo de
 * alertar toda semana — a lição da #620/#622 (alerta repetido vira ruído e
 * ensina a ignorar). Mexer neles só depois de rodar o backtest.
 *
 * Issues: #886 (origem), #275 (relatório de inatividade), #620/#622 (ruído de
 * alerta repetido), #795 (auditoria dos schedulers: avaliação só de "ontem").
 *
 * Falhas conhecidas:
 *  - 2026-10-23 (#795): o detector avaliava SÓ "ontem" — uma execução perdida
 *    (deploy, 500 do Scheduler como no incidente de 22/10) apagava para sempre
 *    o `HABITUAL_DAY_MISSED` daquele dia, e a semanal só existia se a
 *    execução de segunda funcionasse. Corrigido por
 *    {@link evaluateRecentUsagePattern} (reavaliação dos últimos
 *    {@link USAGE_RECHECK_DAYS} dias + último domingo fechado). Mesma classe do
 *    `scanExpiringLabels` (janela fixa sem recuperação).
 *
 * Histórico:
 *  - 2026-10-23 11:48 +0200 — (#795) `evaluateRecentUsagePattern`,
 *    `usageRecentWindowYmds`, `USAGE_RECHECK_DAYS`.
 */

/** Tipos de desvio de padrão detectados. */
export const UsageAlertKindSchema = z.enum(['WEEKLY_DROP', 'WEEKLY_SURGE', 'HABITUAL_DAY_MISSED']);
export type UsageAlertKind = z.infer<typeof UsageAlertKindSchema>;
/** Constantes do conceito — comparar sempre por aqui, nunca pelo literal. */
export const UsageAlertKind = UsageAlertKindSchema.enum;
export const ALL_USAGE_ALERT_KINDS = UsageAlertKindSchema.options;

/**
 * Limiares e chaves de ativação do monitor.
 *
 * Dados — `config/usageAlerts` (Firestore, escrito à mão/admin, lido pelo
 * detector a cada execução). Campo ausente = default; doc ausente = tudo
 * default (inclusive `enabled: true`, `notifyCrm: false`). Exemplo:
 *   { "enabled": true, "notifyCrm": false, "weeklyDropRatio": 0.4 }
 *
 * `notifyCrm` existe para ligar em etapas (#886): primeiro só o resumo ao
 * admin@, depois vendedor/gerente. Quem lê é o trigger do CRM, no momento da
 * notificação — por isso também viaja gravado em cada `usage_alerts`.
 */
export const UsageAlertConfigSchema = z.object({
  enabled: z.boolean().default(true),
  notifyCrm: z.boolean().default(false),
  /** Queda: semana < (1 − x)·base. Owner: 40 %. */
  weeklyDropRatio: z.number().gt(0).lt(1).default(0.4),
  /** Excesso: semana > (1 + x)·base. Owner: 40 %. */
  weeklySurgeRatio: z.number().gt(0).default(0.4),
  /** Semanas anteriores que formam a base (mediana). */
  baselineWeeks: z.number().int().min(2).max(12).default(6),
  /** Mínimo de semanas da base COM impressão — cliente novo não alerta. */
  minActiveBaselineWeeks: z.number().int().min(1).max(12).default(4),
  /** Piso da mediana (etiquetas/semana) — volume ínfimo oscila ±40 % à toa. */
  minWeeklyBaseline: z.number().int().min(0).default(20),
  /** Ocorrências anteriores do mesmo dia da semana consideradas. */
  habitualLookbackWeeks: z.number().int().min(2).max(12).default(8),
  /** Ocorrências com impressão para o dia contar como habitual (6/8 = 75 %). */
  habitualMinHits: z.number().int().min(1).max(12).default(6),
  /** Sequência zerada a partir da qual a #275 já cobre — habitual se cala. */
  inactiveStreakDays: z.number().int().min(1).max(14).default(3),
});
export type UsageAlertConfig = z.infer<typeof UsageAlertConfigSchema>;

/**
 * Combinações que deixariam uma regra MUDA para sempre sem erro nenhum
 * (auditoria de 2026-10-08, #886, A8): exigir mais ocorrências do que se olha.
 * `parseUsageAlertConfig` as trata como config inválida → defaults + log.
 */
const UsageAlertConfigCheckedSchema = UsageAlertConfigSchema.refine(
  (c) => c.habitualMinHits <= c.habitualLookbackWeeks && c.minActiveBaselineWeeks <= c.baselineWeeks,
  { message: 'habitualMinHits ≤ habitualLookbackWeeks e minActiveBaselineWeeks ≤ baselineWeeks' },
);
export const DEFAULT_USAGE_ALERT_CONFIG: UsageAlertConfig = UsageAlertConfigSchema.parse({});

/**
 * Lê a config crua do Firestore sem nunca lançar: config inválida cai nos
 * defaults e devolve `invalid: true` para o chamador reportar. Um typo em
 * `config/usageAlerts` não pode derrubar o job inteiro.
 */
export function parseUsageAlertConfig(raw: unknown): { config: UsageAlertConfig; invalid: boolean } {
  const parsed = UsageAlertConfigCheckedSchema.safeParse(raw ?? {});
  if (parsed.success) return { config: parsed.data, invalid: false };
  return { config: DEFAULT_USAGE_ALERT_CONFIG, invalid: true };
}

/**
 * Status comerciais monitorados. Além dos dois do relatório de inatividade
 * (#275), entram `PARTNER` (cliente de parceiro tem gerente interno a avisar)
 * e `COURTESY` — decisões do owner em 2026-10-08 (#886). A cortesia entrou
 * depois do backtest: o cliente de maior volume da base era cortesia, e sem
 * ela o monitor não teria observado ninguém relevante. Cortesia sem venda no
 * CRM notifica só os admins. Demo, suspenso e sem plano ficam fora — não
 * imprimir ali não é sinal de nada. `TEST` (#1006, tenant de teste ou sandbox)
 * também fica fora.
 */
export const USAGE_MONITORED_STATUSES: readonly TenantDisplayStatus[] = [
  TenantDisplayStatus.ACTIVE,
  TenantDisplayStatus.PAYMENT_PENDING,
  TenantDisplayStatus.PARTNER,
  TenantDisplayStatus.COURTESY,
];

/**
 * Tenants sintéticos que geram uso FALSO e ficam fora do monitor, mesmo com
 * status comercial elegível: o simulador (`LABEL_PURGING_TENANT_IDS`, #354)
 * imprime etiquetas reais por agenda, e os de teste de billing
 * (`TEST_MODE_BILLING_TENANT_IDS`, #258) podem aparecer ACTIVE em sandbox.
 */
export function isUsageMonitorExcludedTenant(tenantId: string): boolean {
  return (
    (LABEL_PURGING_TENANT_IDS as readonly string[]).includes(tenantId) ||
    (TEST_MODE_BILLING_TENANT_IDS as readonly string[]).includes(tenantId)
  );
}

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `yyyy-MM-dd` + n dias (n pode ser negativo). Aritmética civil, sem fuso. */
export function ymdAddDays(ymd: string, n: number): string {
  const m = YMD_RE.exec(ymd);
  if (!m) throw new Error(`ymd inválido: ${ymd}`);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
  return d.toISOString().slice(0, 10);
}

/** Dia da semana de uma data civil: 0 = domingo … 6 = sábado. */
export function weekdayOfYmd(ymd: string): number {
  const m = YMD_RE.exec(ymd);
  if (!m) throw new Error(`ymd inválido: ${ymd}`);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
}

/**
 * Todos os dias que {@link evaluateUsagePattern} pode ler, do mais antigo até
 * `yesterdayYmd` inclusive. O detector busca exatamente esta janela (57 dias
 * com os defaults) — se a regra passar a olhar mais longe, a janela acompanha
 * sozinha.
 */
export function usageWindowYmds(yesterdayYmd: string, cfg: UsageAlertConfig = DEFAULT_USAGE_ALERT_CONFIG): string[] {
  const span = Math.max(7 * (cfg.baselineWeeks + 1), 7 * cfg.habitualLookbackWeeks + 1, cfg.inactiveStreakDays);
  return Array.from({ length: span }, (_, i) => ymdAddDays(yesterdayYmd, i - span + 1));
}

/**
 * Janela de leitura de {@link evaluateRecentUsagePattern}: tudo que a
 * avaliação do último domingo fechado e dos últimos `recheckDays` dias pode ler,
 * do mais antigo até `yesterdayYmd`. Passa de 57 para até 63 dias com os
 * defaults (o último domingo está até 6 dias atrás de ontem).
 *
 * Origem: #795 — 2026-10-23 11:48 +0200. Usado por: `detectTenantUsageAnomalies`.
 */
export function usageRecentWindowYmds(
  yesterdayYmd: string,
  cfg: UsageAlertConfig = DEFAULT_USAGE_ALERT_CONFIG,
  recheckDays: number = USAGE_RECHECK_DAYS,
): string[] {
  const back = recheckDays - 1;
  const span = Math.max(
    weekdayOfYmd(yesterdayYmd) + 7 * (cfg.baselineWeeks + 1), // semanal: domingo fechado + base
    back + 7 * cfg.habitualLookbackWeeks + 1, // habitual do dia mais antigo reavaliado
    back + cfg.inactiveStreakDays, // sequência zerada
  );
  return Array.from({ length: span }, (_, i) => ymdAddDays(yesterdayYmd, i - span + 1));
}

/** Mediana; lista par = média dos dois do meio. Lista vazia = 0. */
export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  const at = (i: number): number => s[i] ?? 0;
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? at(mid) : (at(mid - 1) + at(mid)) / 2;
}

/**
 * Um desvio detectado. `observed`/`baseline` em etiquetas: semana inteira nas
 * regras semanais; no habitual, `observed` = 0 e `baseline` = quantas das
 * ocorrências anteriores tiveram impressão (ex.: 7 de 8).
 */
export type UsageFinding = {
  kind: UsageAlertKind;
  /** Primeiro dia do período (seg da semana, ou o próprio dia no habitual). */
  periodStartYmd: string;
  periodEndYmd: string;
  observed: number;
  baseline: number;
  /** observed / baseline (semanal); hits / lookback (habitual). */
  ratio: number;
  /** 0 = domingo … 6 = sábado; só no habitual. */
  weekday: number | null;
};

export type EvaluateUsageInput = {
  /** Último dia completo, no fuso do tenant (`yyyy-MM-dd`). */
  yesterdayYmd: string;
  /** Impressões do dia; dia sem doc = 0. */
  printedOn: (ymd: string) => number;
  cfg?: UsageAlertConfig;
};

/**
 * Quantos dias (ontem e os anteriores) o scheduler reavalia na regra habitual.
 *
 * Três = o mesmo número de dias zerados a partir do qual o relatório de
 * inatividade (#275) assume (`inactiveStreakDays`): uma execução perdida é
 * recuperada nas duas seguintes, e depois disso o assunto já é do #275.
 * Reavaliar é seguro porque o id do alerta é determinístico (`create()`).
 * Origem: #795 — 2026-10-23 11:48 +0200.
 */
export const USAGE_RECHECK_DAYS = 3;

/**
 * Semana fechada seg–dom que termina em `endYmd` (um domingo): WEEKLY_DROP ou
 * WEEKLY_SURGE contra a mediana das `baselineWeeks` semanas anteriores.
 * Interno — a regra em si não mudou desde a #886, só passou a ser chamável para
 * um domingo que não é "ontem" (#795).
 *
 * Origem: #886 — 2026-10-08 11:41 +0200 (corpo extraído em #795, 2026-10-23 11:48 +0200).
 * Usado por: `evaluateUsagePattern` e `evaluateRecentUsagePattern` (aqui).
 */
function evaluateWeekly(endYmd: string, printedOn: (ymd: string) => number, cfg: UsageAlertConfig): UsageFinding[] {
  const weekTotal = (weekEndYmd: string): number => {
    let sum = 0;
    for (let i = 0; i < 7; i++) sum += printedOn(ymdAddDays(weekEndYmd, -i));
    return sum;
  };
  const observed = weekTotal(endYmd);
  const previous = Array.from({ length: cfg.baselineWeeks }, (_, k) => weekTotal(ymdAddDays(endYmd, -7 * (k + 1))));
  const activeWeeks = previous.filter((w) => w > 0).length;
  const baseline = median(previous);
  if (activeWeeks < cfg.minActiveBaselineWeeks || baseline < cfg.minWeeklyBaseline || baseline <= 0) return [];
  const ratio = observed / baseline;
  const period = { periodStartYmd: ymdAddDays(endYmd, -6), periodEndYmd: endYmd, observed, baseline, ratio, weekday: null };
  if (observed < (1 - cfg.weeklyDropRatio) * baseline) return [{ kind: UsageAlertKind.WEEKLY_DROP, ...period }];
  if (observed > (1 + cfg.weeklySurgeRatio) * baseline) return [{ kind: UsageAlertKind.WEEKLY_SURGE, ...period }];
  return [];
}

/**
 * Habitual para UM dia (`dayYmd`): zerado num dia da semana em que o cliente
 * costuma imprimir e ainda fora da sequência que o #275 já cobre. Interno.
 *
 * Origem: #886 — 2026-10-08 11:41 +0200 (corpo extraído em #795, 2026-10-23 11:48 +0200).
 * Usado por: `evaluateUsagePattern` e `evaluateRecentUsagePattern` (aqui).
 */
function evaluateHabitual(dayYmd: string, printedOn: (ymd: string) => number, cfg: UsageAlertConfig): UsageFinding[] {
  if (printedOn(dayYmd) !== 0) return [];
  let streak = 0;
  while (streak < cfg.inactiveStreakDays && printedOn(ymdAddDays(dayYmd, -streak)) === 0) streak++;
  if (streak >= cfg.inactiveStreakDays) return [];
  let hits = 0;
  for (let k = 1; k <= cfg.habitualLookbackWeeks; k++) if (printedOn(ymdAddDays(dayYmd, -7 * k)) > 0) hits++;
  if (hits < cfg.habitualMinHits) return [];
  return [
    {
      kind: UsageAlertKind.HABITUAL_DAY_MISSED,
      periodStartYmd: dayYmd,
      periodEndYmd: dayYmd,
      observed: 0,
      baseline: hits,
      ratio: hits / cfg.habitualLookbackWeeks,
      weekday: weekdayOfYmd(dayYmd),
    },
  ];
}

/**
 * Aplica as três regras ao histórico de um tenant, avaliando SÓ `yesterdayYmd`.
 * Ver cabeçalho do módulo. O scheduler usa {@link evaluateRecentUsagePattern};
 * esta continua exportada como a avaliação pontual (backtest e testes).
 *
 * Histórico: 2026-10-23 11:48 +0200 — (#795) corpo dividido em
 * `evaluateWeekly`/`evaluateHabitual`, sem mudança de comportamento.
 */
export function evaluateUsagePattern(input: EvaluateUsageInput): UsageFinding[] {
  const cfg = input.cfg ?? DEFAULT_USAGE_ALERT_CONFIG;
  const { yesterdayYmd, printedOn } = input;
  return [
    // Semanal: só fecha a semana no domingo (avaliado na segunda).
    ...(weekdayOfYmd(yesterdayYmd) === 0 ? evaluateWeekly(yesterdayYmd, printedOn, cfg) : []),
    ...evaluateHabitual(yesterdayYmd, printedOn, cfg),
  ];
}

/**
 * Como {@link evaluateUsagePattern}, mas recuperando o que uma execução perdida
 * deixou para trás: habitual para ontem e para os `recheckDays - 1` dias
 * anteriores, e a semanal do ÚLTIMO domingo fechado (não só quando ontem é
 * domingo). Ids determinísticos + `create()` no chamador fazem a reavaliação
 * de um achado já gravado ser um no-op.
 *
 * Origem: #795 — 2026-10-23 11:48 +0200 (auditoria dos 42 schedulers).
 *
 * Por quê: "avalia só ontem" era a mesma classe do `scanExpiringLabels` — se a
 * execução do dia não rodou (incidente de 22/10: 500 do Scheduler), o desvio
 * daquele dia nunca era detectado, e a semanal dependia de a segunda-feira ter
 * funcionado.
 *
 * Contrato: puro. O chamador entrega em `printedOn` as impressões de TODOS os
 * dias de {@link usageRecentWindowYmds} (dia sem doc = 0). Pode devolver o
 * mesmo `(kind, periodStartYmd)` só uma vez. Achados de dias antigos saem na
 * ordem do mais antigo para ontem.
 *
 * Usado por: scheduler `detectTenantUsageAnomalies` (Functions);
 * `usagePattern.test.ts`.
 *
 * Dicas: se um desvio antigo for detectado só agora (execução perdida), o
 * alerta nasce com `createdAt` de hoje — o trigger do CRM o trata como novo,
 * que é o desejado (o vendedor ainda não foi avisado).
 */
export function evaluateRecentUsagePattern(
  input: EvaluateUsageInput & { recheckDays?: number },
): UsageFinding[] {
  const cfg = input.cfg ?? DEFAULT_USAGE_ALERT_CONFIG;
  const { yesterdayYmd, printedOn } = input;
  const recheckDays = Math.max(1, input.recheckDays ?? USAGE_RECHECK_DAYS);
  const out: UsageFinding[] = [];
  // Último domingo ≤ ontem: se ontem é domingo, é ontem (comportamento antigo).
  out.push(...evaluateWeekly(ymdAddDays(yesterdayYmd, -weekdayOfYmd(yesterdayYmd)), printedOn, cfg));
  for (let k = recheckDays - 1; k >= 0; k--) out.push(...evaluateHabitual(ymdAddDays(yesterdayYmd, -k), printedOn, cfg));
  return out;
}

/** Id determinístico do alerta: `create()` com este id é a idempotência do detector. */
export function usageAlertId(kind: UsageAlertKind, tenantId: string, periodStartYmd: string): string {
  return `${kind}:${tenantId}:${periodStartYmd}`;
}

/**
 * Documento de um desvio detectado — um por (tipo, tenant, período).
 *
 * Dados — `usage_alerts/{kind}:{tenantId}:{periodStartYmd}`. Escrito só pelo
 * `detectTenantUsageAnomalies` (Admin SDK, `create()`); o trigger
 * `crmOnUsageAlertCreated` acrescenta `crmNotifiedAt`/`crmNotifiedUids`/
 * `crmTaskId`. Lido por super-admin. Exemplo (anonimizado):
 *   {
 *     "alertId": "HABITUAL_DAY_MISSED:BR-00000000000000:2026-10-07",
 *     "tenantId": "BR-00000000000000", "tenantName": "Restaurante Exemplo",
 *     "kind": "HABITUAL_DAY_MISSED",
 *     "periodStartYmd": "2026-10-07", "periodEndYmd": "2026-10-07",
 *     "observed": 0, "baseline": 7, "ratio": 0.875, "weekday": 3,
 *     "timeZone": "America/Sao_Paulo", "statusLabel": "Ativo",
 *     "notifyCrm": false,          // config no instante da detecção
 *     "adminEmailSent": true,
 *     "createdAt": 1760000000000,  // epoch ms do servidor (Functions)
 *     "crmNotifiedAt": null
 *   }
 */
export const UsageAlertSchema = z.object({
  alertId: z.string().min(1),
  tenantId: z.string().min(1),
  tenantName: z.string(),
  kind: UsageAlertKindSchema,
  periodStartYmd: z.string().regex(YMD_RE),
  periodEndYmd: z.string().regex(YMD_RE),
  observed: z.number().nonnegative(),
  baseline: z.number().nonnegative(),
  ratio: z.number().nonnegative(),
  weekday: z.number().int().min(0).max(6).nullable(),
  timeZone: z.string(),
  /**
   * Status comercial já traduzido no instante da detecção ("Ativo",
   * "Cortesia"…). Gravado no doc porque o resumo ao admin@ é montado a partir
   * dos docs pendentes, que podem ser de dias anteriores (#886, A2).
   */
  statusLabel: z.string(),
  notifyCrm: z.boolean(),
  createdAt: z.number().int().nonnegative(),
  /**
   * O resumo ao admin@ que cita este alerta saiu. Gravado `false` na criação
   * e virado `true` só depois do envio: se a SendGrid falhar, a reexecução do
   * job encontra o doc já criado e ainda o inclui no resumo.
   */
  adminEmailSent: z.boolean(),
  crmNotifiedAt: z.number().int().nonnegative().nullable().optional(),
  crmNotifiedUids: z.array(z.string()).optional(),
  crmTaskId: z.string().nullable().optional(),
});
export type UsageAlert = z.infer<typeof UsageAlertSchema>;

/** Nome do dia em pt-BR (e-mail interno e tarefa do CRM). */
export const WEEKDAY_NAMES_PT_BR = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'] as const;
